import {
  ExtraCoverageKeys,
  extraCoverageKeys,
} from "@covertree/new-business-graphql";
import { ExtraCoverageAccessor } from "./extraCoverages/extraCoverageAccessor";

const isEnumValue = <T extends object>(
  value: unknown,
  enumType: T
): value is T[keyof T] => {
  return Object.values(enumType).includes(value as T[keyof T]);
};

const getEnumValues = <T extends object>(enumObj: T): T[keyof T][] =>
  Object.values(enumObj) as T[keyof T][];

const extraCoverageKeyToAccessorMap: Record<
  ExtraCoverageKeys,
  ExtraCoverageAccessor
> = {
  golfCarts: ExtraCoverageAccessor.GolfCartDamageAndLiabilityCoverage,
  debrisRemoval: ExtraCoverageAccessor.DebrisRemoval,
  earthquake: ExtraCoverageAccessor.EarthquakeCoverage,
  equipmentBreakdown: ExtraCoverageAccessor.EquipmentBreakdown,
  lossAssessment: ExtraCoverageAccessor.LossAssessment,
  residenceBurglary: ExtraCoverageAccessor.ResidenceBurglary,
  secondaryResidenceLiability:
    ExtraCoverageAccessor.SecondaryResidenceLiability,
  scheduledPersonalProperty: ExtraCoverageAccessor.ValuableItemProtection,
  waterBackupAndSumpOverflow: ExtraCoverageAccessor.WaterBackupCoverage,
  enhancedCoverage: ExtraCoverageAccessor.EnhancedCoverage,
  roofPaymentSchedule: ExtraCoverageAccessor.RoofPaymentSchedule,
  tripCollision: ExtraCoverageAccessor.TripCollisionCoverage,
  vacationRental: ExtraCoverageAccessor.VacationRentalCoverage,
  mineSubsidence: ExtraCoverageAccessor.MineSubsidence,
  mineSubsidenceScheduled: ExtraCoverageAccessor.MineSubsidenceScheduled,
  incidentalFarming: ExtraCoverageAccessor.IncidentalFarming,
  landlordPersonalInjury: ExtraCoverageAccessor.LandlordPersonalInjury,
  identityFraud: ExtraCoverageAccessor.IdentityFraudExpense,
  sinkhole: ExtraCoverageAccessor.SinkholeCoverage,
  buildersRisk: ExtraCoverageAccessor.BuildersRisk,
  vandalismOrMaliciousMischief:
    ExtraCoverageAccessor.VandalismOrMaliciousMischief,
  windstormOrHailExclusion: ExtraCoverageAccessor.WindstormOrHailExclusion,
  fortifiedRoofUpgrade: ExtraCoverageAccessor.FortifiedRoofUpgrade,
};

/** Extra coverage accessors in Extra Coverages page order (`extraCoverageKeys`). */
const extraCoveragesPageAccessors: ExtraCoverageAccessor[] =
  extraCoverageKeys.map((key) => extraCoverageKeyToAccessorMap[key]);

/**
 * Extra Coverages page order: RPS first, then locked coverages, then editable ones.
 */
const compareExtraCoveragesOnPage = (
  a: { key: string; isCustomizable?: boolean },
  b: { key: string; isCustomizable?: boolean }
): number => {
  const aIsRoofPaymentSchedule = a.key === "roofPaymentSchedule";
  const bIsRoofPaymentSchedule = b.key === "roofPaymentSchedule";

  if (aIsRoofPaymentSchedule !== bIsRoofPaymentSchedule) {
    return aIsRoofPaymentSchedule ? -1 : 1;
  }

  return Number(a.isCustomizable) - Number(b.isCustomizable);
};

export {
  isEnumValue,
  getEnumValues,
  extraCoverageKeyToAccessorMap,
  extraCoveragesPageAccessors,
  compareExtraCoveragesOnPage,
};
